import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { type PrimitiveAtom, useAtomValue } from "jotai";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "src/i18n";
import { CellElement } from "src/elements/SubgridTable/components/SubgridCell";
import styled from "styled-components";
import { styledSafe } from "src/utils/styledComponent";
import { LandscapeA4WidthInPx } from "./constant";
import { FirstPageSection } from "./FirstPageSection";
import { buildItemSummaryPages } from "./ItemSummaryPage";
import { ItemSummaryPagesList } from "./ItemSummaryPagesList";
import type { StoreProductsMap, StoreDetail } from "./PageMerchflows_ExportPdf_StorePogId";
import { SingleBayPagesList } from "./SingleBayPagesList";
import { buildA4PagesForSingleBay } from "./singleBayUtils";
import {
  DEFAULT_NOTCH_RULER_WIDTH,
  getPdfPlanogramWidth,
} from "src/components/Planogram/components/Ruler/PdfNotchRulers";
import { getDrawnRunWidth } from "src/components/Planogram/components/KonvaPlanogram/sceneProjection";
import type { BayWithA4Pages } from "./types";
import {
  isPdfTableOverflowing,
  measurePdfTable,
  repaginateBayTables,
  repaginateSummaryTables,
} from "./pdfPagination";
import type { ItemSummaryPageRows } from "./ItemSummaryPage";

const PDF_LAYOUT_MAX_PASSES = 4;

const measurePdfTables = (view: HTMLElement) => {
  const bayTables = Array.from(view.querySelectorAll<HTMLElement>("[data-pdf-bay-table]"));
  const summaryPages = Array.from(view.querySelectorAll<HTMLElement>("[data-pdf-summary-page]"));
  const newTables = summaryPages.map((page) => {
    const table = page.querySelector<HTMLElement>('[data-pdf-summary-table="new"]');
    if (!table) throw new Error("New-item PDF summary table is missing");
    return measurePdfTable(table);
  });
  const deletedTables = summaryPages.map((page) => {
    const table = page.querySelector<HTMLElement>('[data-pdf-summary-table="deleted"]');
    if (!table) throw new Error("Deleted-item PDF summary table is missing");
    return measurePdfTable(table);
  });

  return {
    bayTables: bayTables.map(measurePdfTable),
    newTables,
    deletedTables,
  };
};

interface PdfLayout {
  initialBays: BayWithA4Pages[];
  initialSummaryPages: ItemSummaryPageRows[];
  bays: BayWithA4Pages[];
  summaryPages: ItemSummaryPageRows[];
  paginationPass: number;
}

const repaginatePdfLayout = (
  view: HTMLElement,
  source: Omit<PdfLayout, "paginationPass">,
  paginationPass: number,
): PdfLayout => {
  const measurements = measurePdfTables(view);
  return {
    ...source,
    bays: repaginateBayTables(source.bays, measurements.bayTables),
    summaryPages: repaginateSummaryTables(
      source.summaryPages,
      measurements.newTables,
      measurements.deletedTables,
    ),
    paginationPass,
  };
};

const View = styled.div`
  --shelf-color: gray;
  width: ${LandscapeA4WidthInPx};
  height: auto;
  padding-inline: 12px;
  box-sizing: border-box;

  ${CellElement} {
    padding: 2px;
  }
`;

export const PlanogramContainer = styledSafe("div")<{
  isRenderingAsImage?: boolean;
}>`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  user-select: none;
  visibility: ${({ isRenderingAsImage }) => isRenderingAsImage && "hidden"};
`;

export const PlanogramElement = styled.div<{
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
}>`
  width: ${({ width, scaleX }) => `calc(${width}px * ${scaleX})`};
  height: ${({ height, scaleY }) => `calc(${height}px * ${scaleY})`};
  background-color: #fcf7f0;
  position: relative;
`;

interface Props {
  currentStoreCode?: string;
  pogAtom: PrimitiveAtom<Pog | null>;
  storeDetail: StoreDetail | null;
  storeProducts?: StoreProductsMap;
  hasNoDeliveryHistory?: boolean;
  hasComparisonError?: boolean;
  excludedProductCodes?: string[];
  onLayoutReady?: () => void;
  onLayoutError?: (error: Error) => void;
}

interface PlanogramInnerProps extends Props {
  pog: Pog;
}

export const PdfPlanogramPage = (props: Props) => {
  const pog = useAtomValue(props.pogAtom);

  return pog ? (
    <PlanogramInner
      currentStoreCode={props.currentStoreCode}
      pogAtom={props.pogAtom}
      storeDetail={props.storeDetail}
      storeProducts={props.storeProducts}
      hasNoDeliveryHistory={props.hasNoDeliveryHistory}
      hasComparisonError={props.hasComparisonError}
      excludedProductCodes={props.excludedProductCodes}
      onLayoutReady={props.onLayoutReady}
      onLayoutError={props.onLayoutError}
      pog={pog}
    />
  ) : null;
};

const PlanogramInner = ({
  currentStoreCode,
  pogAtom,
  storeDetail,
  storeProducts,
  hasNoDeliveryHistory,
  hasComparisonError,
  excludedProductCodes,
  onLayoutReady,
  onLayoutError,
  pog,
}: PlanogramInnerProps) => {
  const viewRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");
  const { scaleX, scaleY } = useMemo(() => {
    const scale = 492 / pog.planogram.getMaxHeight();
    return { scaleX: scale, scaleY: scale };
  }, [pog]);

  const disableContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const initialBays: BayWithA4Pages[] = useMemo(() => {
    return pog.planogram.bays.map((currentBay) => ({
      bay: currentBay,
      pages: buildA4PagesForSingleBay({
        pog,
        bayId: currentBay.uniqueId,
        section: currentBay.getNo(),
        t,
      }),
    }));
  }, [pog, t]);

  const firstPageWidth = getPdfPlanogramWidth(
    getDrawnRunWidth(pog.planogram),
    pog.planogram.bays.length,
    DEFAULT_NOTCH_RULER_WIDTH,
  );
  const firstPageHeight = pog.planogram.getMaxHeight();
  const firstPageScale =
    firstPageWidth > 0 && firstPageHeight > 0
      ? Math.min(1076 / firstPageWidth, 492 / firstPageHeight)
      : 1;

  const initialSummaryPages = useMemo(() => {
    return buildItemSummaryPages({
      pog,
      storeDetail,
      currentStoreCode,
      storeProducts,
      hasNoDeliveryHistory,
      hasComparisonError,
      excludedProductCodes,
    });
  }, [
    pog,
    storeDetail,
    currentStoreCode,
    storeProducts,
    hasNoDeliveryHistory,
    hasComparisonError,
    excludedProductCodes,
  ]);

  const [layout, setLayout] = useState<PdfLayout | null>(null);
  const currentLayout =
    layout?.initialBays === initialBays && layout.initialSummaryPages === initialSummaryPages
      ? layout
      : null;
  const processedBay = currentLayout?.bays ?? initialBays;
  const itemSummaryPages = currentLayout?.summaryPages ?? initialSummaryPages;

  const updateLayout = useCallback(
    (view: HTMLElement, source: Omit<PdfLayout, "paginationPass">, paginationPass: number) => {
      try {
        setLayout(repaginatePdfLayout(view, source, paginationPass));
      } catch (error) {
        onLayoutError?.(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [onLayoutError],
  );

  useEffect(() => {
    let active = true;
    const measure = async () => {
      await document.fonts?.ready;
      if (!active || !viewRef.current) return;

      updateLayout(
        viewRef.current,
        {
          initialBays,
          initialSummaryPages,
          bays: initialBays,
          summaryPages: initialSummaryPages,
        },
        1,
      );
    };

    void measure();
    return () => {
      active = false;
    };
  }, [initialBays, initialSummaryPages, updateLayout]);

  useLayoutEffect(() => {
    if (!currentLayout || !viewRef.current) return;
    const tables = viewRef.current.querySelectorAll<HTMLElement>(
      "[data-pdf-bay-table], [data-pdf-summary-table]",
    );
    const overflowingTables = Array.from(tables).filter(isPdfTableOverflowing);
    if (overflowingTables.length) {
      if (currentLayout.paginationPass >= PDF_LAYOUT_MAX_PASSES) {
        const table = overflowingTables[0];
        const label =
          table?.dataset.pdfBayTable !== undefined
            ? `bay page ${Number(table.dataset.pdfBayTable) + 2}`
            : `${table?.dataset.pdfSummaryTable ?? "summary"} summary`;
        onLayoutError?.(
          new Error(
            `${label} PDF table still overflows after ${currentLayout.paginationPass} passes`,
          ),
        );
        return;
      }

      updateLayout(viewRef.current, currentLayout, currentLayout.paginationPass + 1);
      return;
    }
    onLayoutReady?.();
  }, [currentLayout, onLayoutError, onLayoutReady, updateLayout]);

  const singleBayPagesFlat = useMemo(() => {
    return processedBay.flatMap((bayWithPages) =>
      bayWithPages.pages.map((page, index) => ({
        bay: bayWithPages.bay,
        page,
        index,
      })),
    );
  }, [processedBay]);

  const itemSummaryStartPageNumber = singleBayPagesFlat.length + 2;
  const totalPages = singleBayPagesFlat.length + 1 + itemSummaryPages.length;

  return (
    <View ref={viewRef} onContextMenuCapture={disableContextMenu}>
      <FirstPageSection
        storeDetail={storeDetail}
        pogAtom={pogAtom}
        firstPageScale={firstPageScale}
        totalPages={totalPages}
        scaleX={scaleX}
        scaleY={scaleY}
      />
      <SingleBayPagesList
        singleBayPagesFlat={singleBayPagesFlat}
        storeDetail={storeDetail}
        scaleX={scaleX}
        scaleY={scaleY}
        pog={pog}
        totalPages={totalPages}
      />
      <ItemSummaryPagesList
        itemSummaryPages={itemSummaryPages}
        storeDetail={storeDetail}
        itemSummaryStartPageNumber={itemSummaryStartPageNumber}
        totalPages={totalPages}
      />
    </View>
  );
};
